import React from "react";
import { Link } from "react-router-dom";
import NewNavbar from "../components/NewNavbar";
import Footer from "../components/Footer";
import {
  useProfileReadQuery,
  useLogoutCreateMutation,
  useGetMyBodyTypeQuery,
  useGetMyFaceShapeQuery,
  useGetMyColorAnalysisQuery,
} from "../store/api.ts";
import { ReactComponent as MeasurementsIcon } from "./../assets/svgs/measurements-icon.svg";
import { ReactComponent as BodyShapeIcon } from "./../assets/svgs/body-shape-icon.svg";
import { ReactComponent as InseamIcon } from "./../assets/svgs/inseam-icon.svg";
import { ReactComponent as SizeIcon } from "./../assets/svgs/size-icon.svg";
import { ReactComponent as SkinIcon } from "./../assets/svgs/skin-icon.svg";
import { ReactComponent as WeightIcon } from "./../assets/svgs/weight-icon.svg";
import { ReactComponent as ItemNotFoundIcon } from "./../assets/svgs/item-not-found.svg";
import SEO from "../components/SEO.js";

const normalizeShapeResponse = (data) => {
  if (Array.isArray(data)) return data[0] || null;
  return data || null;
};

const normalizeColors = (items) => {
  if (!Array.isArray(items)) return [];

  return items
    .map((item) => ({
      name: item?.color_name || "",
      color: item?.color_hex || "",
    }))
    .filter((item) => item.name && item.color);
};

const normalizeRecommendations = (items) => {
  if (!Array.isArray(items)) return [];

  return items
    .slice(0, 4)
    .map((item) => ({
      title: item?.title || "",
      image_url: item?.image_url || "",
    }))
    .filter((item) => item.title || item.image_url);
};

const getBodyDressRecommendations = (bodyShape) => {
  if (!Array.isArray(bodyShape?.categories)) return [];

  const dressesCategory = bodyShape.categories.find((category) =>
      String(category?.category_name || "").trim().toUpperCase() === "DRESSES",
  );

  return normalizeRecommendations(dressesCategory?.recommended);
};

const valueOrDash = (value) => value || "—";

const titleCase = (value) => {
  if (!value) return "—";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const isMaleProfile = (profile) =>
  String(profile?.gender || "").toLowerCase() === "male";

const metricCards = (profile) => [
  {
    label: "Height",
    value:
      profile?.measurement_system === "imperial"
        ? `${valueOrDash(profile?.height)}"`
        : `${valueOrDash(profile?.height)} cm`,
    Icon: MeasurementsIcon,
  },
  {
    label: "Weight",
    value:
      profile?.measurement_system === "imperial"
        ? `${valueOrDash(profile?.weight)} lbs`
        : `${valueOrDash(profile?.weight)} kg`,
    Icon: WeightIcon,
  },
  {
    label: "Waist",
    value: profile?.waist_size ? `${profile.waist_size}"` : "—",
    Icon: InseamIcon,
  },
  isMaleProfile(profile)
    ? {
        label: "Eye Colour",
        value: titleCase(profile?.eye_color),
        Icon: SkinIcon,
      }
    : {
        label: "Bra Size",
        value:
          profile?.bra_band_size || profile?.bra_cup_size
            ? `${profile?.bra_band_size || ""}${String(
                profile?.bra_cup_size || "",
              ).toUpperCase()}`
            : "—",
        Icon: SizeIcon,
      },
  {
    label: "Body Shape",
    value: titleCase(profile?.body_type),
    Icon: BodyShapeIcon,
  },
  {
    label: "Skin Color",
    value: titleCase(profile?.skin_tone),
    Icon: SkinIcon,
  },
];

function SmallMetricCard({ label, value, Icon }) {
  return (
    <div className="relative rounded-[5px] border border-[#00000033] bg-white px-3 py-2">
      <p className="mb-[3px] font-spaceMono text-[8px] font-normal uppercase tracking-[0.22em] text-[#6A7282]">
        [{label}]
      </p>

      <div className="flex flex-row items-center justify-between gap-3">
        <p className="font-montserrat text-[15px] font-medium text-black">
          {value}
        </p>

        {Icon && <Icon className="h-4 w-4 shrink-0 text-[#A0A0A0]" />}
      </div>
    </div>
  );
}

function ColorSwatch({ name, color }) {
  return (
    <div className="mt-3">
      <div
        className="mb-[10px] h-[80px] rounded-[5px] border border-[#D8D8D8]"
        style={{ backgroundColor: color }}
      />
      <p className="font-spaceMono text-[7px] md:text-[9px] font-normal uppercase tracking-[0.14em] text-[#6A7282]">
        {name}
      </p>
    </div>
  );
}

const ViewAllColorSwatch = ({ count, onClick }) => (
  <button type="button" onClick={onClick} className="mt-3 w-full self-start text-left">
    <div className="mb-[10px] flex h-[80px] w-full items-center justify-center rounded-[5px] border border-[#D8D8D8] bg-[#F4F4F4] transition hover:bg-[#EAEAEA]">
      <span className="font-montserrat text-[20px] font-medium text-black">
        +{count}
      </span>
    </div>
    <p className="font-spaceMono text-[7px] font-normal uppercase tracking-[0.14em] text-[#6A7282] md:text-[9px]">
      View All
    </p>
  </button>
);

const ColorPaletteModal = ({ title, colors, onClose }) => {
  if (!colors?.length) return null;
  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 px-4"
      onClick={onClose}>
      <div
        className="max-h-[85vh] w-full max-w-[720px] overflow-y-auto rounded-[5px] bg-white p-4 md:p-6"
        onClick={(event) => event.stopPropagation()}>
        <div className="mb-5 flex items-center justify-between border-b border-[#0000001A] pb-4">
          <div>
            <p className="mb-1 font-spaceMono text-[8px] font-normal uppercase tracking-[0.24em] text-[#6A7282] md:text-[10px]">
              Color Analysis
            </p>
            <h3 className="font-montserrat text-[24px] font-bold uppercase leading-none tracking-[-0.04em] text-black md:text-[32px]">
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close color palette"
            className="flex h-9 w-9 items-center justify-center rounded-[3px] border border-[#D8D8D8] font-helvetica text-[20px] text-black hover:bg-[#F4F4F4]"
          >
            ×
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {colors.map(({ name, color }, index) => (
            <ColorSwatch
              key={`${name}-${color}-${index}`}
              name={name}
              color={color}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const RecommendationCard = ({ title, image_url, index }) => (
  <div className="relative rounded-[3px] border border-[#D8D8D8] bg-[#F1F1F1] p-[8px]">
    <div className="mb-[10px] flex items-center justify-between">
      <p className="font-spaceMono text-[8px] md:text-[9px] uppercase tracking-[0.22em] text-[#6A7282] font-normal">
        [CFG_{index}]
      </p>
      <span className="h-[5px] w-[5px] bg-[#8A8A8A]" />
    </div>
    <div className="h-[170px] w-full overflow-hidden rounded-[2px] bg-white">
      {image_url && (
        <img
          src={image_url}
          alt={title}
          loading="lazy"
          className="h-full w-full object-contain"
        />
      )}
    </div>

    <div className="mt-[10px] pb-[7px]">
      <p className="font-montserrat text-[12px] font-bold uppercase leading-tight tracking-[0.03em] text-black md:text-[14px]">
        {title}
      </p>
    </div>
  </div>
);

function SectionCard({ eyebrow, title, children }) {
  return (
    <section className="rounded-[5px] border border-[#D0D0D0] bg-white p-3 md:p-4">
      <p className="mb-5 font-spaceMono text-[9px] font-bold uppercase tracking-[0.24em] text-[#6A7282]">
        &gt; {eyebrow}
      </p>
      {title && (
        <h2 className="mb-4 font-montserrat text-[34px] font-bold uppercase leading-[0.9] tracking-[-0.06em] text-black md:text-[42px]">
          {title}
        </h2>
      )}
      {children}
    </section>
  );
}

const LoadingSpinner = ({ size = "h-4 w-4" }) => (
  <span
    className={`${size} inline-block shrink-0 animate-spin rounded-full border-2 border-[#D8D8D8] border-t-[#202020]`}
    aria-hidden="true"
  />
);

const ProfileStylingPage = () => {
  const { data: profile, isLoading } = useProfileReadQuery();
  const { data: bodyShapeResponse, isLoading: isBodyShapeLoading, isError: isBodyShapeError} = useGetMyBodyTypeQuery();
  const { data: faceShapeResponse, isLoading: isFaceShapeLoading, isError: isFaceShapeError} = useGetMyFaceShapeQuery();
  const { data: colorAnalysisResponse, isLoading: isColorAnalysisLoading, isError: isColorAnalysisError} = useGetMyColorAnalysisQuery();
  const [activeColorPalette, setActiveColorPalette] = React.useState(null);
  const bodyShape = normalizeShapeResponse(bodyShapeResponse);
  const faceShape = normalizeShapeResponse(faceShapeResponse);
  const colorAnalysis = normalizeShapeResponse(colorAnalysisResponse);
  const bodyRecommendations = getBodyDressRecommendations(bodyShape);
  const faceRecommendations = normalizeRecommendations(faceShape?.recommended_necklines);
  const neutralColors = normalizeColors(colorAnalysis?.best_neutrals);
  const accentColors = normalizeColors(colorAnalysis?.best_colors);

  const displayName =
    profile?.first_name || profile?.last_name
      ? `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim()
      : "Profile";

  const profileImage =
    profile?.survey_headshot_url || profile?.survey_full_body_url || "";
  const [logout] = useLogoutCreateMutation();

  const handleLogout = async () => {
    try {
      await logout().unwrap();
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      localStorage.removeItem("authToken");
      window.location.href = "/";
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F4] text-black">
      <SEO
        title="Profile Styling | Your Eveness Style Profile"
        description="View your Eveness style profile, body shape, face shape, color palette, measurements, and personalized styling recommendations."
        path="/account/profile"
      />
      <NewNavbar bottomNav="account" />

      <main className="mx-auto w-full max-w-[1080px] px-3 pb-10 pt-8">
        {isLoading ? (
          <div className="rounded border border-[#D0D0D0] bg-white p-8 text-center font-helvetica text-sm">
            Loading profile styling...
          </div>
        ) : (
          <>
            <header className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-[182px_1fr] md:gap-6">
              <div className="mx-auto h-[132px] w-[132px] overflow-hidden rounded-[2px] bg-[#777] md:mx-0 md:h-[182px] md:w-[182px]">
                {profileImage && (
                  <img
                    src={profileImage}
                    alt={displayName}
                    className="h-full w-full object-cover"
                  />
                )}
              </div>

              <div className="flex flex-col justify-center text-center md:text-left">
                <p className="font-spaceMono text-[10px] font-normal uppercase tracking-[0.24em] text-[#6A7282] md:text-[12px]">
                  ID: 684-CH
                </p>

                <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                  <div className="min-w-0">
                    <h1 className="break-words font-helvetica text-[36px] font-medium uppercase leading-none tracking-[-2px] text-black sm:text-[44px] md:text-[64px] md:tracking-[-3.6px] lg:text-[52px]">
                      {displayName}
                    </h1>

                    <p className="mt-2 font-inter text-[8px] font-normal uppercase tracking-[0.06em] text-[#6A7282] sm:text-[10px] md:text-[12px]">
                      &gt; Style profile and measurements initialized.
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Link
                      to="/account"
                      className="mx-auto flex h-[42px] w-full max-w-[280px] items-center justify-center rounded-[5px] bg-[#202020] font-helvetica text-[14px] font-medium text-white hover:bg-black md:mx-0 md:w-auto md:min-w-[240px]"
                    >
                      Edit Metrics
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="mx-auto flex h-[42px] w-full max-w-[280px] items-center justify-center rounded-[5px] bg-[#202020] font-helvetica text-[14px] font-medium text-white hover:bg-black md:mx-0 md:w-auto md:min-w-[240px]"
                    >
                      Log out
                    </button>
                  </div>
                </div>
              </div>
            </header>

            <div className="mb-4 grid grid-cols-1 gap-2 md:grid-cols-3 border border-[#0000001A] bg-[#0000000D] rounded-[5px] p-[17px]">
              {metricCards(profile).map((item) => (
                <SmallMetricCard
                  key={item.label}
                  label={item.label}
                  value={item.value}
                  Icon={item.Icon}
                />
              ))}
            </div>

            <SectionCard eyebrow="Module: Color Palette">
              <h2 className="mb-3 font-montserrat text-[32px] font-bold uppercase leading-none tracking-[-0.07em] text-black md:text-[42px] md:tracking-[-2.4px]">
                Analysis: {titleCase(colorAnalysis?.name)}
              </h2>

              <div className="flex flex-col items-start">
                <div className="mb-4 flex w-fit max-w-full items-center gap-2 rounded-[5px] border border-[#0000001A] bg-white px-3 py-[14px] font-spaceMono text-[8px] font-normal uppercase tracking-[0.12em] text-[#6A7282] md:text-[10px]">
                  <span>Profile detected:</span>
                  {isColorAnalysisLoading ? (
                    <LoadingSpinner size="h-3 w-3" />
                  ) : (
                    <span>{colorAnalysis?.summary || "—"}</span>
                  )}
                </div>

                <button className="mb-5 h-[42px] min-w-[240px] items-center justify-center rounded-[5px] bg-[#202020] font-helvetica text-[14px] font-medium text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-60">
                  Full Guide Coming Soon
                </button>
              </div>

              {isColorAnalysisLoading ? (
                <div className="flex items-center justify-center gap-2 py-10 font-spaceMono text-[10px] uppercase tracking-[0.16em] text-[#6A7282]">
                  <LoadingSpinner />
                  <span>Loading color analysis...</span>
                </div>
              ) : isColorAnalysisError ? (
                <div className="py-10 text-center font-spaceMono text-[10px] uppercase tracking-[0.16em] text-[#6A7282]">
                  Unable to load color analysis.
                </div>
              ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-[5px] border border-[#0000001A] p-[10px]">
                  <p className="mb-3 border-b border-[#0000001A] pb-3 font-spaceMono text-[8px] font-normal uppercase tracking-[0.24em] text-[#6A7282] md:text-[10px]">
                    Primary Neutrals
                  </p>
                  <div className="grid grid-cols-5 items-start gap-2">
                    {neutralColors.slice(0, 4).map(({ name, color }, index) => (
                      <ColorSwatch key={name} name={name} color={color} />
                    ))}
                  {neutralColors.length > 4 && (
                    <ViewAllColorSwatch
                      count={neutralColors.length - 4}
                      onClick={() =>
                        setActiveColorPalette({
                          title: "Primary Neutrals",
                          colors: neutralColors,
                        })
                      }
                    />
                  )}
                  </div>
                </div>

                <div className="rounded-[5px] border border-[#0000001A] p-[10px]">
                  <p className="mb-3 border-b border-[#0000001A] pb-3 font-spaceMono text-[8px] font-normal uppercase tracking-[0.24em] text-[#6A7282] md:text-[10px]">
                    Accent Highlights
                  </p>
                  <div className="grid grid-cols-5 items-start gap-2">
                    {accentColors.slice(0, 4).map(({ name, color }, index) => (
                      <ColorSwatch key={name} name={name} color={color} />
                    ))}
                    {accentColors.length > 4 && (
                      <ViewAllColorSwatch
                        count={accentColors.length - 4}
                        onClick={() =>
                          setActiveColorPalette({
                            title: "Accent Highlights",
                            colors: accentColors,
                          })
                        }
                      />
                    )}
                  </div>
                  </div>
                </div>
              )}
            </SectionCard>

            <div className="mt-4">
              <SectionCard eyebrow="Module: Form_Factor">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_1.35fr]">
                  <div>
                    <h2 className="mb-5 font-helvetica text-[42px] font-black uppercase leading-[0.9] tracking-[-0.07em] text-black">
                      Shape: {titleCase(profile?.body_type)}
                    </h2>

                    <p className="mb-1 font-inter text-[13px] leading-[1.5]">
                      &gt;{" "}
                      <span className="font-bold md:text-[13px] text-[11px] text-black">
                        Objective:
                      </span>{" "}
                      {bodyShape?.headline || "—"}
                    </p>
                    <p className="mb-5 font-inter text-[13px] leading-[1.5]">
                      &gt;{" "}
                      <span className="font-bold md:text-[13px] text-[11px] text-black">
                        Execution:
                      </span>{" "}
                      {bodyShape?.summary || "—"}
                    </p>

                    <button className="mb-5 h-[42px] min-w-[240px] items-center justify-center rounded-[5px] bg-[#202020] font-helvetica text-[14px] font-medium text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-60">
                      Full Guide Coming Soon
                    </button>
                  </div>

                  <div>
                    <p className="mb-3 font-spaceMono text-[8px] md:text-[9px] font-bold uppercase tracking-[0.24em] text-[#6A7282]">
                      Recommended Configurations
                    </p>
                    {isBodyShapeLoading ? (
                      <div className="flex items-center justify-center gap-2 py-10 font-spaceMono text-[10px] uppercase tracking-[0.16em] text-[#6A7282]">
                        <LoadingSpinner />
                        <span>Loading recommendations...</span>
                      </div>
                    ) : isBodyShapeError ? (
                      <div className="mx-auto w-full max-w-[520px] text-center">
                        <div className="mx-auto flex h-[88px] w-[88px] items-center justify-center rounded-full bg-[#F4F4F4]">
                          <ItemNotFoundIcon />
                        </div>
                        <h1 className="mt-6 font-helvetica text-[16px] font-semibold text-[#0F0F0F] sm:text-[20px]">
                        Unable to load recommendations.
                        </h1>
                      </div>
                    ) : bodyRecommendations.length ? (
                      <div className="grid grid-cols-2 gap-3">
                        {bodyRecommendations.map((recommendation,index) => (
                          <RecommendationCard
                            key={`${recommendation.title}-${recommendation.image_url}`}
                            title={recommendation.title}
                            image_url={recommendation.image_url}
                            index={index+1}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="mx-auto w-full max-w-[520px] text-center">
                        <div className="mx-auto flex h-[88px] w-[88px] items-center justify-center rounded-full bg-[#F4F4F4]">
                          <ItemNotFoundIcon />
                        </div>
                        <h1 className="mt-6 font-helvetica text-[16px] font-semibold text-[#0F0F0F] sm:text-[20px]">
                          No recommendations available.
                        </h1>
                      </div>
                    )}
                  </div>
                </div>
              </SectionCard>
            </div>

            <div className="mt-4">
              <SectionCard eyebrow="Digital: Face_Geometry">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_1.35fr]">
                  <div>
                    <h2 className="mb-5 font-helvetica text-[42px] font-black uppercase leading-[0.9] tracking-[-0.07em] text-black">
                      Face: {titleCase(profile?.face_shape)}
                    </h2>

                    <p className="mb-3 font-helvetica text-[12px] leading-[1.5]">
                      <strong>Objective:</strong>{" "}
                      {faceShape?.headline || "—"}
                    </p>
                    <p className="mb-5 font-helvetica text-[12px] leading-[1.5]">
                      <strong>Execution:</strong>{" "}
                      {faceShape?.summary || "—"}
                    </p>

                    <button className="mb-5 h-[42px] min-w-[240px] items-center justify-center rounded-[5px] bg-[#202020] font-helvetica text-[14px] font-medium text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-60">
                      Full Guide Coming Soon
                    </button>
                  </div>

                  <div>
                    <p className="mb-3 font-spaceMono text-[8px] font-bold uppercase tracking-[0.24em] text-[#6A7282] md:text-[9px]">
                      Recommended Configurations
                    </p>
                    {isFaceShapeLoading ? (
                      <div className="flex items-center justify-center gap-2 py-10 font-spaceMono text-[10px] uppercase tracking-[0.16em] text-[#6A7282]">
                        <LoadingSpinner />
                        <span>Loading recommendations...</span>
                      </div>
                    ) : isFaceShapeError ? (
                      <div className="mx-auto w-full max-w-[520px] text-center">
                        <div className="mx-auto flex h-[88px] w-[88px] items-center justify-center rounded-full bg-[#F4F4F4]">
                          <ItemNotFoundIcon />
                        </div>
                        <h1 className="mt-6 font-helvetica text-[16px] font-semibold text-[#0F0F0F] sm:text-[20px]">
                          Unable to load recommendations.
                        </h1>
                      </div>
                    ) : faceRecommendations.length ? (
                      <div className="grid grid-cols-2 gap-3">
                        {faceRecommendations.map((recommendation, index) => (
                          <RecommendationCard
                            key={`${recommendation.title}-${recommendation.image_url}`}
                            title={recommendation.title}
                            image_url={recommendation.image_url}
                            index={index + 1}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="mx-auto w-full max-w-[520px] text-center">
                        <div className="mx-auto flex h-[88px] w-[88px] items-center justify-center rounded-full bg-[#F4F4F4]">
                          <ItemNotFoundIcon />
                        </div>
                        <h1 className="mt-6 font-helvetica text-[16px] font-semibold text-[#0F0F0F] sm:text-[20px]">
                          No recommendations available.
                        </h1>
                      </div>
                    )}
                  </div>
                </div>
              </SectionCard>
            </div>
          </>
        )}
      </main>
      {activeColorPalette && (
        <ColorPaletteModal
          title={activeColorPalette.title}
          colors={activeColorPalette.colors}
          onClose={() => setActiveColorPalette(null)}
        />
      )}

      <Footer />
    </div>
  );
};

export default ProfileStylingPage;
